import React, { useMemo, useRef, useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../home.css";

const VideoSlider = ({ selectedTab }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [loadedVideos, setLoadedVideos] = useState({});
  const videoRefs = useRef({});
  const [lang, setLang] = useState("en");

  // to avoid Focusable descendents within an [aria-hidden="true"]
  const sliderRef = useRef(null);

  const fixSlickAriaHidden = () => {
    if (!sliderRef.current) return;

    const root = sliderRef.current.innerSlider?.list; // slick "list" DOM node
    if (!root) return;

    // Make hidden slide interactive elements unfocusable
    root
      .querySelectorAll(
        '.slick-slide[aria-hidden="true"] a, .slick-slide[aria-hidden="true"] button, .slick-slide[aria-hidden="true"] [tabindex]',
      )
      .forEach((el) => el.setAttribute("tabindex", "-1"));

    // Restore focus for visible slides
    root
      .querySelectorAll(
        '.slick-slide[aria-hidden="false"] a, .slick-slide[aria-hidden="false"] button',
      )
      .forEach((el) => el.removeAttribute("tabindex"));
  };

  useEffect(() => {
    // run once after first render
    setTimeout(fixSlickAriaHidden, 0);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setLang(e.detail.lang || "en");
    };

    window.addEventListener("languageChanged", handler);
    return () => window.removeEventListener("languageChanged", handler);
  }, []);

  const videosData = {
    en: {
      0: [
        { youtubeId: "K-irgyUFdOE", title: "International Departure" },
        { youtubeId: "_TMGGUenTcs", title: "Domestic Departure" },
        { youtubeId: "ZEMc_dI8fv8", title: "PRM Assistance" },
      ],
      1: [
        { youtubeId: "ZTeqJQO1o2A", title: "International Arrival" },
        { youtubeId: "4DP0lhjdFNU", title: "Domestic Arrival" },
        { youtubeId: "ZEMc_dI8fv8", title: "PRM Assistance" },
      ],
      2: [
        {
          youtubeId: "Sf7a9Ju5xaM",
          title: "Domestic to International Transfer",
        },
        {
          youtubeId: "uJx3Gp8R63A",
          title: "International to Domestic Transfer",
        },
        { youtubeId: "ZEMc_dI8fv8", title: "PRM Assistance" },
      ],
    },

    hi: {
      0: [
        {
          youtubeId: "yBOzril278g",
          title: "अंतरराष्ट्रीय प्रस्थान (International Departure)",
        },
        {
          youtubeId: "VhMpadIhvpE",
          title: "घरेलू प्रस्थान (Domestic Departure)",
        },
      ],

      1: [
        {
          youtubeId: "Go2FM7kFuAQ",
          title: "अंतरराष्ट्रीय आगमन (International Arrival)",
        },
        {
          youtubeId: "d4O7sdS--Qk",
          title: "घरेलू आगमन (Domestic Arrival)",
        },
        {
          youtubeId: "F8sSmszNHZc",
          title: "घरेलू आगमन और ट्रांसफर",
        },
      ],

      2: [
        {
          youtubeId: "EZrk6jD3qRY",
          title: "घरेलू से अंतरराष्ट्रीय ट्रांसफर (D → I)",
        },
        {
          youtubeId: "Fy9W1Yd3XPI",
          title: "घरेलू से घरेलू ट्रांसफर (D → D)",
        },
        {
          youtubeId: "FRNFzWN6jZs",
          title: "अंतरराष्ट्रीय से अंतरराष्ट्रीय ट्रांसफर (I → I)",
        },
        {
          youtubeId: "bEI3Yn5TJJs",
          title: "अंतरराष्ट्रीय से घरेलू ट्रांसफर (I → D)",
        },
      ],
    },
  };

  // const currentVideos = useMemo(() => {
  //   return videosData[selectedTab] || [];
  // }, [selectedTab]);
  const currentVideos = useMemo(() => {
    return videosData[lang]?.[selectedTab] || videosData.en[selectedTab] || [];
  }, [selectedTab, lang]);

  // Reset active slide and pause all videos when tab changes
  useEffect(() => {
    setActiveSlide(0);
    // Pause all videos when tab changes
    Object.values(videoRefs.current).forEach((iframe) => {
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*",
        );
      }
    });
  }, [selectedTab]);

  const getThumbnailUrl = (video) => {
    let videoId = null;
    if (typeof video === "string") {
      videoId = getYouTubeId(video);
    } else if (video.youtubeId) {
      videoId = video.youtubeId;
    } else if (video.url) {
      videoId = getYouTubeId(video.url);
    }
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // const getEmbedUrl = (video) => {
  //   let videoId = null;
  //   if (typeof video === "string") {
  //     videoId = getYouTubeId(video);
  //   } else if (video.youtubeId) {
  //     videoId = video.youtubeId;
  //   } else if (video.url) {
  //     videoId = getYouTubeId(video.url);
  //   }

  //   if (!videoId) return null;

  //   return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=0&rel=0&modestbranding=1`;
  // };

  const getEmbedUrl = (video, autoplay = false) => {
    let videoId = null;
    if (typeof video === "string") {
      videoId = getYouTubeId(video);
    } else if (video.youtubeId) {
      videoId = video.youtubeId;
    } else if (video.url) {
      videoId = getYouTubeId(video.url);
    }

    if (!videoId) return null;

    const base = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1`;
    return autoplay ? `${base}&autoplay=1` : `${base}&autoplay=0`;
  };

  // Function to pause a video by index
  const pauseVideo = (index) => {
    const iframe = videoRefs.current[`${selectedTab}-${index}`];
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        "*",
      );
    }
  };

  // Handle slide change - pause previous video
  const handleBeforeChange = (oldIndex, newIndex) => {
    // Pause the previous video
    pauseVideo(oldIndex);
    setActiveSlide(newIndex);
  };

  const settings = {
    dots: true,
    infinite: currentVideos.length > 1,
    speed: 600,
    slidesToShow: 1,
    autoplaySpeed: 8000,
    slidesToScroll: 1,
    autoplay: true,
    fade: true,
    cssEase: "ease-in-out",
    arrows: true,
    swipe: true,
    touchMove: true,
    draggable: true,
    swipeToSlide: true,
    beforeChange: handleBeforeChange,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: true,
          arrows: true,
          fade: true,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          dots: true,
          fade: true,
        },
      },
    ],
    afterChange: () => fixSlickAriaHidden(),
  };

  if (!currentVideos || currentVideos.length === 0) {
    return null;
  }

  return (
    <div className="w-full py-[8vw] xl:py-[4vw] bg-white">
      <div className="lg:w-[90%] mx-auto relative lg:px-[8vw] px-[5%]">
        <div className="video-slider-container">
          <Slider key={selectedTab} {...settings}>
            {currentVideos.map((video, index) => {
              const videoKey = `${selectedTab}-${index}`;
              const isLoaded = !!loadedVideos[videoKey];
              const embedUrl = getEmbedUrl(video, isLoaded);
              const thumbnailUrl = getThumbnailUrl(video);

              const title =
                typeof video === "string"
                  ? `Video ${index + 1}`
                  : video.title || `Video ${index + 1}`;

              if (!embedUrl) return null;

              const handleClick = () => {
                setLoadedVideos((prev) => ({ ...prev, [videoKey]: true }));
              };

              return (
                <div key={videoKey} className="px-[1vw] xl:px-[0.5vw]">
                  <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-[1vw] xl:rounded-[1vh] bg-black">
                    {!isLoaded ? (
                      <button
                        type="button"
                        onClick={handleClick}
                        className="absolute top-0 left-0 w-full h-full group"
                      >
                        {thumbnailUrl && (
                          <img
                            src={thumbnailUrl}
                            alt={title}
                            className="w-full h-full object-cover"
                          />
                        )}
                        {/* Dark overlay */}
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition" />
                        {/* Play icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <div className="ml-1 w-0 h-0 border-t-[10px] border-b-[10px] border-l-[16px] border-t-transparent border-b-transparent border-l-[#002C62]" />
                          </div>
                        </div>
                      </button>
                    ) : (
                      <iframe
                        ref={(el) => (videoRefs.current[videoKey] = el)}
                        className="absolute top-0 left-0 w-full h-full"
                        src={embedUrl}
                        title={`${title} - (${lang})`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default VideoSlider;
